import React, { useState, useEffect } from 'react'
import { Box, useTheme, Tabs, Tab, Alert, Button, Stack, Typography, Tooltip, SvgIcon } from "@mui/material";
import { tokens } from "../../assets/theme";
import { experimentalStyled as styled, alpha } from '@mui/material/styles';
import { FormattedMessage, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchMachineInfo, fetchMachineData, patchMachineData, fetchMachineStops, fetchActivitesMachine, fetchActivitesMachineAndDates, fetchMachineStop } from "../../fetchers/machine";
import { getStopReasonCodes } from "../stopReasonCodes/hooks/fetchers";
import { getGanttDataMachine } from "../gantt/hooks/fetchers"
import { fetchModules } from "../../fetchers/modules";
import { isModuleEnabled } from "../../hooks/useModules";
import { CMMS_MODULE } from "../cmms/cmmsFeature";
import { getReadingTypes } from "../../fetchers/readingTypes";
import { pushErrorMessage, pushNotification } from '../../utils/notifications';
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { fetchAreas } from "../../fetchers/area";
import { getTimespan } from "../../fetchers/timespan";
import MachineStopGrid from "./components/machineStopsGrid";
import GroupedStopsChart from "./components/GroupedStopsChart";
import CountedStopsChart from "./components/CountedStopsChart";
import MachineSettingsDialog from "./components/machineSettingsDialog";
import MachineCommentDialog from "./components/machineCommentDialog";
import messages from "./components/messages";
import Popup from "../../components/Popup";
import Infoboxes from "./components/Infoboxes";
import AxionControls from "../../components/controls/AxionControls";
import Paper from '@mui/material/Paper';
import 'react-toastify/dist/ReactToastify.css';
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import PlclogDialog from '../plclog/components/plclogDialog';
import { fetchCompany } from '../../fetchers/company';
import NewActivityDialog from './components/newActivityDialog';
import { fetchMachineTypes } from '../../fetchers/machineType';
import useFeatureFlags, { isFeatureEnabled } from '../../hooks/useFeatureFlags';
import AutoCodeDialog from './components/autoCodeDialog';
import { patchMachineAutocode, fetchUncodedStopsAndStatus } from "../../fetchers/machine";
import AutoCodeBanner from './components/autoCodeBanner';
import { fetchSpeedUnitTypes } from '../../fetchers/speedUnitTypes';
import AlarmBanner from './components/alarmBanner';
import MachineStopBanner from './components/machineStopBanner';
import MachineTabs, { CMMS_WORK_ORDERS_TAB_INDEX, useIsCmmsWorkOrderTabAvailable } from './components/machineTabs';
import MachineWorkOrdersTab from './components/MachineWorkOrdersTab';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import BarChartIcon from '@mui/icons-material/BarChart';
import IconButton from '@mui/material/IconButton';
import ActivitiesGrid from '../activities/components/activitiesGrid';
import StartStopPO from './components/startStopPO';
import AlarmsGrid from '../activities/components/alarmsGrid';
import MachinePace from './components/machinePace';
import SensorData from './components/sensorData';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import MachineGantt from './components/machineGantt';
import MachineStopEditDialog from "../machineDetails/components/machineStopEditDialog";
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import MachineInSetupBanner from './components/machineInSetupBanner';
import MachineInSetupOverlay from './components/machineInSetupOverlay';
import { useOeeNotificationsForMachine } from '../../hooks/useOeeNotifications';

//ICONS
const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
    ...theme.typography.body2,
    padding: theme.spacing(1),
    textAlign: 'center',
    color: theme.palette.text.secondary,
}));

const StyledTabs = styled((props) => (

    <Tabs
        {...props}
        TabIndicatorProps={{ children: <span className="MuiTabs-indicatorSpan" /> }}
    />
))({
    '& .MuiTabs-indicator': {
        display: 'flex',
        justifyContent: 'center',
        backgroundColor: 'transparent',
    },
    '& .MuiTabs-indicatorSpan': {
        maxWidth: 40,
        width: '100%',
        backgroundColor: '#799c4a',
    },
});

const StyledTab = styled((props) => <Tab disableRipple {...props} />)(

    ({ theme }) => ({

        textTransform: 'none',
        fontWeight: theme.typography.fontWeightRegular,
        fontSize: theme.typography.pxToRem(20),
        marginRight: theme.spacing(1),
        //backgroundColor: '#799c4a',
        color: 'rgba(255, 255, 255, 0.7)',
        '&.Mui-selected': {
            color: '#fff',
            backgroundColor: "#434957 !important",
        },
        '&.Mui-focusVisible': {
            backgroundColor: 'rgba(100, 95, 228, 0.32)',
        },
        '&.MuiTab-root': {
            backgroundColor: '#0c101b',
            borderTopLeftRadius: '5px',
            borderTopRightRadius: '5px',
        },

    }),
);

dayjs.extend(utc);
dayjs.extend(timezone);
const inTz = (d, tz) => dayjs(d).tz(tz);

const MachineDetails = (props) => {
    const { id } = useParams();
    const [companyKey, setCompanyKey] = useLocalStorage("companyKey", 1);
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [machineId, setMachineId] = useState(id);
    const [openSettingsPopup, setOpenSettingsPopup] = useState(false);
    const [openCommentPopup, setOpenCommentPopup] = useState(false);
    const [openPlclogPopup, setopenPlclogPopup] = useState(false);
    const [openAutoCodePopup, setOpenAutoCodePopup] = useState(false);
    const [machineSettings, setMachineSettings] = useState({});
    const [showBackdrop, setShowBackdrop] = useState(false);
    const [selectedTabDefault, setSelectedTabDefault] = useLocalStorage("machine-details-selectedTabDefault", 1);
    const [selectedTab, setSelectedTab] = React.useState(selectedTabDefault);
    const [machineComment, setMachineComment] = useState({ id: 0, comment: "", commentedBy: "", updatedBy: "" });
    const [timeSpan, setTimeSpan] = useLocalStorage("timespan", "currentshift");
    const useDateInterval = timeSpan === 'manual';
    const [timeSpanStartDate, setTimeSpanStartDate] = React.useState(null);
    const [timeSpanEndDate, setTimeSpanEndDate] = React.useState(null);
    const [startDate, setStartDate] = useLocalStorage("startDate", dayjs());
    const [endDate, setEndDate] = useLocalStorage("endDate", dayjs());
    const [currentStartDate, setCurrentStartDate] = React.useState("currentStartDate", dayjs());
    const [currentEndDate, setCurrentEndDate] = React.useState("currentEndDate", dayjs());
    const [newActivityPopup, setNewActivityPopup] = useState(false);
    const [machineStopPopup, setMachineStopPopup] = useState(false);
    const [nrOfActivities, setNrOfActivities] = useState(-1);
    const [machineAlarms, setMachineAlarms] = useState({});
    const [pausedMachineAlarms, setPausedMachineAlarms] = useState({});
    const [activityFilter, setActivityFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const { formatMessage } = useIntl();
    const [tabExpanded, setTabExpanded] = useLocalStorage("machine-details-tabExpanded", false);
    const [showStopCharts, setShowStopCharts] = useLocalStorage("machine-details-showStopCharts", true);
    const [searchParams] = useSearchParams();
    const [filteredStopReasonCodes, setFilteredStopReasonCodes] = React.useState([]);
    const [machineStop, setMachineStop] = React.useState(null);
    const [isActive, setIsActive] = useState(true);
    const [lastTimeSpanChange, setLastTimeSpanChange] = useLocalStorage("lastTimeSpanChange", null);
    const [initialErrorTime, setInitialErrorTime] = useState("");
    const [errorLatched, setErrorLatched] = useState(false);
    const [dismissedSetupOverlay, setDismissedSetupOverlay] = useState(false);
    const [reportPO, setReportPO] = useState(false);

    const maxStopRows = 10500;

    const queryClient = useQueryClient();



    useEffect(() => {
        const tabParam = searchParams.get("tab");

        if (tabParam !== null) {
            const parsed = Number(tabParam);
            setSelectedTab(Number.isNaN(parsed) ? selectedTabDefault : parsed);
        } else {
            setSelectedTab(selectedTabDefault);
        }
    }, [searchParams, selectedTabDefault])

    const { available: cmmsWorkOrderTabAvailable, isResolved: cmmsWorkOrderTabResolved } =
        useIsCmmsWorkOrderTabAvailable();

    /*
     * The CMMS tab is the only one that is conditionally rendered rather than disabled, and the
     * selection is persisted in localStorage and settable from `?tab=`. So a user who selects it
     * and then loses the module or the role comes back to an index that no longer exists —
     * no tab highlighted, empty body, nothing to click. Same family as the reorderColumns
     * crash: a saved value naming something that is gone.
     *
     * Waits for the module query to settle, otherwise every reload with the tab selected bounces
     * to tab 1 and back as /api/modules arrives.
     */
    useEffect(() => {
        if (!cmmsWorkOrderTabResolved) return;
        if (selectedTab === CMMS_WORK_ORDERS_TAB_INDEX && !cmmsWorkOrderTabAvailable) {
            setSelectedTab(1);
            setSelectedTabDefault(1);
        }
    }, [cmmsWorkOrderTabResolved, cmmsWorkOrderTabAvailable, selectedTab, setSelectedTabDefault])

    useOeeNotificationsForMachine(companyKey, machineId);

    const machineInfoQuery = useQuery(
        ["machineInfo", machineId],
        () => fetchMachineInfo(machineId),
        {
            retry: true,
            retryDelay: (attempt) =>
                Math.min(1000 * Math.pow(2, attempt), 30_000),
            staleTime: 60_000,
            refetchInterval: 60_000,
            refetchIntervalInBackground: true,
            keepPreviousData: true
        }
    );

    const datesForTimespanQuery = useQuery(
        ["datesForTimespan", timeSpan, companyKey],
        () => getTimespan(timeSpan, companyKey),
        {
            retry: true,
            retryDelay: (attempt) =>
                Math.min(1000 * Math.pow(2, attempt), 30_000),
            staleTime: 120_000,
            refetchInterval: 120_000,
            refetchIntervalInBackground: true,
            keepPreviousData: true
        }
    );

    const datesForCurrentShiftQuery = useQuery(
        ["datesForCurrentShift", companyKey],
        () => getTimespan("currentshift", companyKey),
        {
            retry: true,
            retryDelay: (attempt) =>
                Math.min(1000 * Math.pow(2, attempt), 30_000),
            staleTime: 60_000,
            refetchInterval: 60_000,
            refetchIntervalInBackground: true,
            keepPreviousData: true
        }
    );

    const machineTypes = useQuery(
        ["machineTypes"],
        () => fetchMachineTypes(),
        {
            retry: true,
            retryDelay: (attempt) =>
                Math.min(1000 * Math.pow(2, attempt), 30_000),
            staleTime: 120_000,
            refetchInterval: 120_000,
            refetchIntervalInBackground: true,
            keepPreviousData: true
        }
    );

    const activitiesQuery = useQuery(
        ["machineActivities", machineId],
        () => fetchActivitesMachine(machineId),
        {
            retry: true,
            retryDelay: (attempt) =>
                Math.min(1000 * Math.pow(2, attempt), 30_000),
            staleTime: 60_000,
            refetchInterval: 60_000,
            refetchIntervalInBackground: true,
            keepPreviousData: true
        }
    );

    const activitiesByDateQuery = useQuery(
        ["machineActivitiesByDate", machineId, timeSpanStartDate, timeSpanEndDate],
        () => fetchActivitesMachineAndDates(machineId, timeSpanStartDate, timeSpanEndDate),
        {
            retry: true,
            retryDelay: (attempt) =>
                Math.min(1000 * Math.pow(2, attempt), 30_000),
            staleTime: 60_000,
            refetchInterval: 60_000,
            refetchIntervalInBackground: true,
            enabled: timeSpanEndDate !== null && timeSpanStartDate !== null,
            keepPreviousData: true
        },
    );

    const companyQuery = useQuery(
        ["company", companyKey], () => fetchCompany(companyKey),
        {
            retry: true,
            retryDelay: (attempt) =>
                Math.min(1000 * Math.pow(2, attempt), 30_000),
            staleTime: 60_000,
            refetchInterval: 60_000,
            refetchIntervalInBackground: true,
            keepPreviousData: true
        }
    );

    const uncodedStopsAndStatusQuery = useQuery(
        ["uncodedStopsAndStatus", companyKey, timeSpanStartDate, timeSpanEndDate], 
        () => fetchUncodedStopsAndStatus(timeSpanStartDate, timeSpanEndDate, companyKey),
        {
            retry: true,
            retryDelay: (attempt) =>
                Math.min(1000 * Math.pow(2, attempt), 30_000),
            staleTime: 60_000,
            refetchInterval: 60_000,
            refetchIntervalInBackground: true,
            enabled: timeSpanStartDate !== null && timeSpanEndDate !== null,
            keepPreviousData: true
        }
    );

    const handleSelectedTabChange = (newValue) => {
        setSelectedTab(newValue);
        setSelectedTabDefault(newValue);
    };

    const featuresQuery = useFeatureFlags();
    const navigate = useNavigate();

    useEffect(() => {
        //if selected tab is production orders but the feature is disabled set selected tab to 1
        if (featuresQuery.data) {
            if (selectedTab === 2 && !isFeatureEnabled(featuresQuery, "UseProductionOrderTableAsPOMaster")) {
                handleSelectedTabChange(1);
            }
        }
    }, [selectedTab, featuresQuery.data]);

    const machinestopsQuery = useQuery(
        ["machinestops", machineId, startDate, endDate, useDateInterval, timeSpanStartDate, timeSpanEndDate],
        () => fetchMachineStops(machineId, startDate, endDate, useDateInterval, timeSpanStartDate, timeSpanEndDate, maxStopRows),
        {
            retry: true,
            retryDelay: (attempt) =>
                Math.min(1000 * Math.pow(2, attempt), 30_000),
            staleTime: 60_000,
            refetchInterval: 60_000,
            refetchIntervalInBackground: true,
            enabled: timeSpanEndDate !== null && timeSpanStartDate !== null,
            keepPreviousData: true
        }
    );

    const stopReasonCodesQuery = useQuery(
        ["stopReasonCodes", companyKey],
        () => getStopReasonCodes(companyKey),
        {
            retry: true,
            retryDelay: (attempt) =>
                Math.min(1000 * Math.pow(2, attempt), 30_000),
            staleTime: 120_000,
            refetchInterval: 120_000,
            refetchIntervalInBackground: true,
            keepPreviousData: true
        }
    );

    const aresQuery = useQuery(
        ["areas"],
        () => fetchAreas(),
        {
            retry: true,
            retryDelay: (attempt) =>
                Math.min(1000 * Math.pow(2, attempt), 30_000),
            staleTime: 60_000,
            refetchInterval: 60_000,
            refetchIntervalInBackground: true,
            keepPreviousData: true
        });

    const machineQuery = useQuery(
        ["machine", machineId],
        () => fetchMachineData(machineId),
        {
            retry: true,
            retryDelay: (attempt) =>
                Math.min(1000 * Math.pow(2, attempt), 30_000),
            staleTime: 60_000,
            refetchInterval: 60_000,
            refetchIntervalInBackground: true,
            keepPreviousData: true
        });

    const speedUnitTypesQuery = useQuery(
        ["speedUnitTypes", true],
        () => fetchSpeedUnitTypes(true),
        {
            retry: true,
            retryDelay: (attempt) =>
                Math.min(1000 * Math.pow(2, attempt), 30_000),
            staleTime: 60_000,
            refetchInterval: 60_000,
            refetchIntervalInBackground: true,
            keepPreviousData: true
        });

    const gridInitialState = {
        sorting: {
            sortModel: [
                { field: "created", sort: "desc" }
            ],
        },
    }

    const readingTypesQuery = useQuery(
        ["readingTypes"],
        () => getReadingTypes(),
        {
            retry: true,
            retryDelay: (attempt) =>
                Math.min(1000 * Math.pow(2, attempt), 30_000),
            staleTime: 120_000,
            refetchInterval: 120_000,
            refetchIntervalInBackground: true,
            keepPreviousData: true
        }
    );

    const modulesQuery = useQuery(
        ["module"],
        () => fetchModules(),
        {
            retry: true,
            retryDelay: (attempt) =>
                Math.min(1000 * Math.pow(2, attempt), 30_000),
            staleTime: 120_000,
            refetchInterval: 120_000,
            refetchIntervalInBackground: true,
            keepPreviousData: true
        }
    );

    const ganttQuery = useQuery(
        ["gantt", id],
        () => getGanttDataMachine(id),
        {
            retry: true,
            retryDelay: (attempt) =>
                Math.min(1000 * Math.pow(2, attempt), 30_000),
            staleTime: 60_000,
            refetchInterval: 60_000,
            refetchIntervalInBackground: true,
            keepPreviousData: true
        }
    );

    const criticalQueries = [
        machineInfoQuery,
        datesForTimespanQuery,
        datesForCurrentShiftQuery,
        machineTypes,
        activitiesQuery,
        activitiesByDateQuery,
        companyQuery,
        machinestopsQuery,
        stopReasonCodesQuery,
        aresQuery,
        machineQuery,
        speedUnitTypesQuery,
        readingTypesQuery,
        ganttQuery
    ];

    useEffect(() => {
        if (criticalQueries.some(q => q.failureCount > 0)) {
            setErrorLatched(true);
        }
    }, [criticalQueries.map(q => q.failureCount).join("|")]);

    useEffect(() => {
        if (criticalQueries.every(q => q.isSuccess)) {
            setErrorLatched(false);
        }
    }, [criticalQueries.map(q => q.isSuccess).join("|")]);

    const isInitialLoading = !errorLatched && criticalQueries.some(q => q.isLoading && !q.data && q.failureCount === 0);

    const firstErrorQuery =
        criticalQueries.find((q) => q.failureCount > 0 && q.error) ||
        criticalQueries.find((q) => q.isError && q.error);
    const firstError = firstErrorQuery?.error

    let criticalErrorMessage = null;

    if (firstError) {
        criticalErrorMessage =
            firstError?.response?.data?.title ??
            firstError?.response?.data?.detail ??
            firstError?.message ??
            null;
    }

    const handleRetryAll = () => {
        criticalQueries.forEach(q => q.refetch());
    };

    
    useEffect(() => {
        if (machineQuery.data?.machineInSetup) {
            setDismissedSetupOverlay(false);
        }
    }, [machineQuery.data?.machineInSetup, machineQuery.data?.machineInSetupUntil]);

    useEffect(() => {
        if (errorLatched && initialErrorTime === "") {
            setInitialErrorTime(dayjs().format("YYYY-MM-DD HH:mm:ss"));
        }
    }, [errorLatched, initialErrorTime]);

    useEffect(() => {
        if (!errorLatched && initialErrorTime !== "") {
            setInitialErrorTime("");
        }
    }, [errorLatched, initialErrorTime]);

    useEffect(() => {
        let machine = machineQuery.data;
        if (machine?.changeMachineTabOnInactivity) {
            let timer;

            const resetTimer = () => {
                setIsActive(true);
                clearTimeout(timer);
                timer = setTimeout(() => {
                    const isRunning = machineQuery.data?.machineStatus === "Running";
                    setIsActive(false);
                    console.log("Inactivity detected. Changing tab.", isRunning, machine);
                    setSelectedTab(isRunning ? machine?.changeToTabIfRunning : machine?.changeToTabIfStopped);
                }, machine?.inactivityTime * 1000);
            };

            // Återställ timern vid aktivitet
            window.addEventListener("mousemove", resetTimer);
            window.addEventListener("keydown", resetTimer);

            resetTimer(); // Starta timern

            return () => {
                clearTimeout(timer);
                window.removeEventListener("mousemove", resetTimer);
                window.removeEventListener("keydown", resetTimer);
            };
        }
    }, [machineQuery.isInitialLoading]);

    useEffect(() => {
        if (machineQuery.isFetched) {
            let machine = machineQuery?.data;
            if (machine?.resetTimespanOnShiftChange) {
                const tz = companyQuery.data?.timezone;
                const currentShift = datesForCurrentShiftQuery.data?.[0];

                if (
                    timeSpan !== "currentshift" &&
                    datesForCurrentShiftQuery.isFetched &&
                    companyQuery.isFetched &&
                    tz &&
                    lastTimeSpanChange &&
                    currentShift?.fromDate
                ) {
                    const lastChangeInCompanyTz = dayjs.utc(lastTimeSpanChange).tz(tz);
                    const currentShiftStartDate = dayjs.tz(currentShift.fromDate, tz);

                    if (lastChangeInCompanyTz.isBefore(currentShiftStartDate)) {
                        setTimeSpan("currentshift");
                        setLastTimeSpanChange(dayjs().toISOString());
                        pushNotification(formatMessage(messages.changingToActiveShift));
                    }
                }
            }
        }
    }, [
        timeSpan,
        lastTimeSpanChange,
        companyQuery.isFetched,
        companyQuery.data?.timezone,
        datesForCurrentShiftQuery.isFetched,
        datesForCurrentShiftQuery.dataUpdatedAt,
        machineQuery.data?.resetTimespanOnShiftChange,
        machineQuery.isFetched
    ]);

    useEffect(() => {
        if (timeSpan === 'manual') {
            setTimeSpanStartDate(startDate);
            setTimeSpanEndDate(endDate);
            setCurrentStartDate(startDate);
            setCurrentEndDate(endDate);
        }
    }, [startDate, endDate, timeSpan]);

    useEffect(() => {
        if (datesForTimespanQuery.isFetched && timeSpan !== 'manual' && !datesForTimespanQuery.isError && datesForTimespanQuery.data !== undefined) {
            setTimeSpanStartDate(datesForTimespanQuery.data[0].fromDate);
            setTimeSpanEndDate(datesForTimespanQuery.data[0].toDate);
            setCurrentStartDate(datesForTimespanQuery.data[0].fromDate);
            setCurrentEndDate(datesForTimespanQuery.data[0].toDate);
        }
    }, [datesForTimespanQuery]);

    useEffect(() => {
        if (activitiesQuery.isFetched && activitiesQuery?.data !== undefined) {
            let activities = activitiesQuery.data.filter(x => x.type === 1);
            if (activities.length > 0 && nrOfActivities < activities.length) {
                if (nrOfActivities >= 0) {
                    setNewActivityPopup(true);
                }
            }
            setNrOfActivities(activities.length);

            //Get alarms
            let alarms = activitiesQuery.data.filter(x => x.type === 2 && x.activityStatus.id === 1);
            setMachineAlarms(alarms || [])
            let pausedAlarms = activitiesQuery.data.filter(x => x.type === 2 && x.activityStatus.id === 2);
            setPausedMachineAlarms(pausedAlarms || [])
        }
    }, [activitiesQuery?.data]);

    const activitiesEnabled = modulesQuery.data?.enabledModules.find(module => module.id === "activities") !== undefined;
    // Read here and threaded to Infoboxes, the way activitiesEnabled is threaded to MachineTabs.
    // Infoboxes takes its other switches as props too, and its featureFlags prop no longer
    // answers this one now that CMMS is a module rather than a feature flag.
    const cmmsEnabled = isModuleEnabled(modulesQuery, CMMS_MODULE);
    const handleTimeSpan = (ts) => {
        // Update state or perform any other action with childData
        setTimeSpan(ts);
    };

    const handleOpenActivitiesTab = () => {
        setSelectedTab(1);
    }

    const handleSplit = (id) => {
        navigate(`/machinestops/${id}/split`);
    }

    const handleMachineStopClick = (id) => {
        setShowBackdrop(true);
        fetchMachineStop(id).then((machineStopItem) => {
            fetchMachineData(machineStopItem.machineId).then((machine) => {
                setFilteredStopReasonCodes(stopReasonCodesQuery.data.filter(x => machine.machineStopReasonCodes.some(y => x.id === y.stopReasonCodeId)));
                setMachineStop(machineStopItem);
                setMachineStopPopup(true);
                setShowBackdrop(false);
            }
            ).catch((error) => {
                console.log(error);
                setShowBackdrop(false);
                pushErrorMessage(`Could not get info about the machine connected to the stop.`);
            })
        }).catch((error) => {
            console.log(error);
            setShowBackdrop(false);
            pushErrorMessage(`Could not get info about the stop.`);
        })
    };


    const handleSettingsSave = () => {
        setShowBackdrop(true);
        patchMachineData(machineSettings).then(() => {
            setOpenSettingsPopup(false);
            machineQuery.refetch();
            machineInfoQuery.refetch();
            setShowBackdrop(false);
        })
            .catch((error) => {
                console.log(error);
                setShowBackdrop(false);
                pushErrorMessage(`Could not update the machine settings.`);
            });
    }

    const handleSettingsCancel = () => {
        setOpenSettingsPopup(false);
    }

    const handleEditSettingsClick = () => {
        setMachineSettings(machineQuery.data)
        setOpenSettingsPopup(true);
    }

    const handleCommentClick = () => {
        setOpenCommentPopup(true);
    }

    const handleCommentCancel = () => {
        setOpenCommentPopup(false);
    }

    const handlePlcLogClick = () => {
        setopenPlclogPopup(true);
    }

    const handleAutocodeClick = () => {
        setOpenAutoCodePopup(true);
    }

    const handleCancelAutocodeClick = () => {
        setShowBackdrop(true);
        let autocodeSettingToSave = {
            id: machineId,
            autocodeStopReasonCodeId: null,
            autocodeUntil: null
        }
        patchMachineAutocode(autocodeSettingToSave).then((response) => {
            handleRefresh();
            setOpenAutoCodePopup(false);
            setShowBackdrop(false);
        })
            .catch((error) => {
                console.log(error);
                setShowBackdrop(false);
                pushErrorMessage(`Could not update the stop.`);
            });
    }

    const handleRefresh = () => {
        machineQuery.refetch();
        machineInfoQuery.refetch();
    }

    const invalidateMachineInfo = () => {
        queryClient.invalidateQueries(["machineInfo", machineId]);
    }
        

    const handleStopRefresh = () => {
        ganttQuery.refetch();
        machinestopsQuery.refetch();
    }

    let filteredActivitiesResult = activitiesByDateQuery.data?.filter((activity) => {
        if (activityFilter === "") return true;
        else if (activity?.machine?.machineName?.toLowerCase().includes(activityFilter.toLowerCase())) return true;
        else if (activity?.machine?.machineType?.machineTypeName?.toLowerCase().includes(activityFilter.toLowerCase())) return true;
        else if (activity?.title?.toLowerCase().includes(activityFilter.toLowerCase())) return true;
        else if (activity?.tool?.name?.toLowerCase().includes(activityFilter.toLowerCase())) return true;
        else if (activity?.productionItem?.itemNo?.toLowerCase().includes(activityFilter.toLowerCase())) return true;
        return false;
    });

    filteredActivitiesResult = filteredActivitiesResult?.filter((activity) => {
        if (statusFilter === "") return true;
        else if (activity?.activityStatusId === statusFilter) return true;
        return false;
    });

    let content = "<></>";
    let machineName = "";
    let machineDescription = "";
    let comment = "";
    let machineInfo = {};
    let filteredStopData = {
        isLoading: false,
        isError: false,
        isFetched: false,
        isSuccess: false,
        data: []
    };

    if (isInitialLoading) {
        content = <AxionControls.AxionBackdrop open={true} />
    }
    else if (errorLatched) {
        content = (
            <Box sx={{ height: "95vh", p: 3 }}>
                <Box
                    sx={{
                        maxWidth: 640,
                        mx: "auto",
                        mt: 10,
                        p: 3,
                        borderRadius: 2,
                        backgroundColor: colors.primary[400],
                        boxShadow: 3,
                    }}
                >
                    <Stack spacing={2}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <ErrorOutlineIcon
                                sx={{ fontSize: 26, color: theme.palette.error.main }}
                            />
                            <Typography variant="h6">
                                <FormattedMessage {...messages.loadErrorTitle} />
                            </Typography>
                        </Stack>

                        <Typography variant="body2" sx={{ opacity: 0.85 }}>
                            <FormattedMessage {...messages.loadErrorBody} />
                        </Typography>

                        <Typography
                            variant="caption"
                            sx={{ opacity: 0.7, fontFamily: "monospace" }}
                        >
                            {`${formatMessage({ ...messages.lastAttemptTime })} ${dayjs().format('YYYY-MM-DD HH:mm:ss')}`}<br />
                            {initialErrorTime && `${formatMessage({ ...messages.initialErrorTime })} ${initialErrorTime}`}<br />
                            {criticalErrorMessage}
                        </Typography>

                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                            <Button variant="contained" onClick={handleRetryAll}>
                                <FormattedMessage {...messages.retry} />
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={() => navigate("/machines")}
                                sx={{
                                    color: colors.primary[200],
                                    textTransform: "none",
                                    fontWeight: 400,
                                    "&:hover": {
                                        backgroundColor: alpha(colors.primary[100], 0.08),
                                    },
                                }}
                            >
                                <FormattedMessage {...messages.backToList} />
                            </Button>
                        </Stack>
                    </Stack>
                </Box>
            </Box>
        );
    }
    else {
        const machineIdNumber = Number(machineId);
        const machineStatusForMachine = uncodedStopsAndStatusQuery.data?.find(status => status.machineId === machineIdNumber);
        
        let filteredStopReasonCodes = [];
        if (machineQuery.isFetched && stopReasonCodesQuery.isFetched) {
            filteredStopReasonCodes = stopReasonCodesQuery.data.filter(x => machineQuery.data?.machineStopReasonCodes.some(y => x.id === y.stopReasonCodeId));
        }

        machineInfo = machineInfoQuery.data[0];
        machineName = machineQuery.data?.machineName;
        machineDescription = machineInfoQuery.data[0]?.description;

        if (machineQuery.data !== undefined) {
            comment = machineQuery.data?.comment
        }

        let autocodeStopReasonCode;
        if (machineQuery.data.autocodeStopReasonCodeId !== null) {
            autocodeStopReasonCode = stopReasonCodesQuery.data?.find(item => item.id === machineQuery.data.autocodeStopReasonCodeId)
        }

        filteredStopData = {
            isLoading: machinestopsQuery?.isLoading,
            isError: machinestopsQuery?.isError,
            isFetched: machinestopsQuery?.isFetched,
            isSuccess: machinestopsQuery?.isSuccess,
            data: selectedTab === 0 ? machinestopsQuery.data?.filter(item => item.isUndefined === true) : machinestopsQuery.data
        };

        let currentStop = stopReasonCodesQuery.data?.find(item => item.name === machineInfo.currentStop);
        content =
            <Box sx={{ backgroundColor: colors.primary[700], padding: "10px", paddingRight: "30px", height: "95vh" }}>
                {/* GRID & CHARTS */}

                {machineQuery.data.machineInSetup && !dismissedSetupOverlay && (
                    <MachineInSetupOverlay
                        machineQuery={machineQuery}
                        machineName={machineName}
                        allowDismiss={true}
                        onClose={() => setDismissedSetupOverlay(true)}
                    />
                )}

                {machineQuery.data.autocodeStopReasonCodeId !== null &&
                    <AutoCodeBanner
                        autocodeStopReasonCode={autocodeStopReasonCode}
                        machineQuery={machineQuery}
                        handleCancelAutocodeClick={handleCancelAutocodeClick}
                        companyKey={companyKey}
                    />
                }

                {machineQuery.data.machineInSetup &&
                    <MachineInSetupBanner
                        machineQuery={machineQuery}
                        companyKey={companyKey}
                        onClick={() => setDismissedSetupOverlay(false)}
                    />
                }

                {machineAlarms.length > 0 &&
                    <AlarmBanner
                        alarms={machineAlarms}
                        machineQuery={machineQuery}
                        readingTypesResult={readingTypesQuery}
                        statusId={1}
                    />
                }

                {pausedMachineAlarms.length > 0 &&
                    <AlarmBanner
                        alarms={pausedMachineAlarms}
                        machineQuery={machineQuery}
                        readingTypesResult={readingTypesQuery}
                        statusId={2}
                    />
                }

                { (machineStatusForMachine ? machineStatusForMachine.machineStatus === "Stopped" : machineInfo.machineStatus === "Stopped") &&
                    <MachineStopBanner
                        stopCodeName={machineInfo.currentStop}
                        stopCodeColor={currentStop?.colorCode}
                    />
                }
                <Infoboxes
                    machineId={machineId}
                    machineComment={comment}
                    machineInfo={machineInfo}
                    companyKey={companyKey}
                    timeSpan={timeSpan}
                    machineName={machineName}
                    company={companyQuery?.data}
                    useDateInterval={useDateInterval}
                    startDate={currentStartDate}
                    endDate={currentEndDate}
                    machine={machineQuery?.data}
                    handlePlcLogClick={handlePlcLogClick}
                    handleOpenSettingsClick={handleEditSettingsClick}
                    handleOpenCommentClick={handleCommentClick}
                    handleOpenAutocodeClick={handleAutocodeClick}
                    handleTimeSpan={handleTimeSpan}
                    datePickerStartDate={startDate}
                    datePickerEndDate={endDate}
                    setStartDate={setStartDate}
                    setEndDate={setEndDate}
                    tabExpanded={tabExpanded}
                    featureFlags={featuresQuery}
                    cmmsEnabled={cmmsEnabled}
                    machineStatusForMachine={machineStatusForMachine}
                    invalidateMachineInfo={invalidateMachineInfo}
                    setSelectedTab={handleSelectedTabChange}
                    setReportPO={setReportPO}
                    machineDescription={machineDescription}
                />

                <Box
                    display="flex"
                    alignItems="center"
                >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <MachineTabs
                            machineId={machineId}
                            selectedTab={selectedTab}
                            setSelectedTab={handleSelectedTabChange}
                            handleOpenActivitiesTab={handleOpenActivitiesTab}
                            activitiesEnabled={activitiesEnabled}
                            activitiesCount={nrOfActivities}
                            alarmsCount={machineAlarms.length + pausedMachineAlarms.length}
                        />
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "end", gap: "8px", paddingRight: "20px" }}>
                        {selectedTab === 1 && <Tooltip title={formatMessage(showStopCharts ? messages.hideStopCharts : messages.showStopCharts)}>
                            <IconButton
                                aria-label={formatMessage(showStopCharts ? messages.hideStopCharts : messages.showStopCharts)}
                                aria-pressed={showStopCharts}
                                sx={{
                                    "&:hover": {
                                        backgroundColor: colors.primary[300],
                                    },
                                    backgroundColor: colors.primary[400],
                                    width: "40px",
                                    height: "40px"
                                }}
                                onClick={() => setShowStopCharts(!showStopCharts)}
                            >
                                {showStopCharts ? <SvgIcon fontSize="inherit">
                                    <path d="M5 9.2V20h3V12.2L5 9.2zM10.5 14.7V20h3V17.7l-3-3zM13.5 12.1V4h-3v5.1l3 3zM16 14.6V8h3v9.6l-3-3z" />
                                    <path d="M3 3l18 18" fill="none" stroke="currentColor" strokeWidth="2" />
                                </SvgIcon> : <BarChartIcon fontSize="inherit" />}
                            </IconButton>
                        </Tooltip>}
                        <IconButton
                            aria-label="Fullscreen"

                            sx={{
                                "&:hover": {
                                    backgroundColor: colors.primary[300],
                                },
                                backgroundColor: colors.primary[400],
                                width: "40px",
                                height: "40px"
                            }}
                            onClick={() => { setTabExpanded(!tabExpanded); }}
                        >
                            {tabExpanded ? <CloseFullscreenIcon fontSize="inherit" /> : <OpenInFullIcon fontSize="inherit" />}
                        </IconButton>
                    </Box>
                </Box>

                {selectedTab === 0 &&
                    <Box
                        display="grid"
                        gridTemplateColumns="repeat(12, 1fr)"
                        gap="10px"
                    >
                        <Box
                            gridColumn="span 6"
                        >
                            <MachineStopGrid
                                machinestops={filteredStopData}
                                machine={machineQuery.data}
                                refetchActivities={activitiesQuery?.refetch}
                                tabExpanded={tabExpanded}
                                handleRefresh={handleStopRefresh}
                                height={tabExpanded ? "75vh" : "56vh"}
                                fields={{
                                    machineName: false,
                                    fromDateTimeZone: true,
                                    toDateTimeZone: true,
                                    minutesDown: true,
                                    stopReasonCodeName: true,
                                    itemId: false,
                                    productionOrderId: false,
                                    comment: true,
                                    split: true
                                }}
                            />
                        </Box>
                        <Box
                            gridColumn="span 6"
                        >
                            <ActivitiesGrid
                                activitiesResult={activitiesQuery}
                                filteredResult={filteredActivitiesResult}
                                InitialState={gridInitialState}
                                activityFilter={activityFilter}
                                setActivityFilter={setActivityFilter}
                                statusFilter={statusFilter}
                                setStatusFilter={setStatusFilter}
                                hideStatusFilter={false}
                                hideToolbar={true}
                                tabExpanded={tabExpanded}
                                height={tabExpanded ? "75vh" : "56vh"}
                                fields={{
                                    machineName: false,
                                    machineTypeName: false,
                                    toolName: false,
                                    itemNo: false,
                                    title: true,
                                    created: true,
                                    text: true,
                                    closed: false,
                                    timeLeft: true,
                                    late: true,
                                    closedBy: false,
                                    maxClosedDate: false,
                                    edit: true
                                }}
                            />

                        </Box>
                    </Box>
                }

                {selectedTab === 1 &&
                    <Box
                        display="grid"
                        gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                        gridAutoRows={tabExpanded ? "460px" : "360px"}
                        gap="10px"
                    >
                        <Box
                            gridColumn={showStopCharts ? "span 8" : "span 12"}
                            gridRow="span 2"
                            marginBottom={2}
                            minWidth={0}
                        >
                            <Box sx={{ backgroundColor: colors.primary[400] }}>
                                <MachineGantt
                                    key={showStopCharts ? "with-stop-charts" : "full-width"}
                                    data={ganttQuery?.data}
                                    startDate={currentStartDate}
                                    endDate={currentEndDate}
                                    company={companyKey}
                                    chartContainer={`chart-machinegantt`}
                                    featureFlags={featuresQuery?.data}
                                    divClass="machine-stop-minigantt"
                                    showHeader={true}
                                    handleClick={handleMachineStopClick}
                                />
                            </Box>
                            <MachineStopGrid
                                machinestops={machinestopsQuery}
                                machine={machineQuery.data}
                                refetchActivities={activitiesQuery?.refetch}
                                tabExpanded={tabExpanded}
                                handleRefresh={handleStopRefresh}
                            />
                        </Box>
                        {showStopCharts && <Box id="machine-stop-charts"
                            gridColumn="span 4"
                            gridRow="span 2"
                            minWidth={0}
                            display="grid"
                            gridTemplateRows="repeat(2, 1fr)"
                            gap="10px">
                            <Box height={tabExpanded ? "460px" : "360px"}
                                backgroundColor={colors.primary[400]}>
                                <Box width="100%" height="100%" paddingTop="10px">
                                    <GroupedStopsChart data={machinestopsQuery} xAxisLabel={formatMessage(messages.downtimeInMinutes)} timeSpanStartDate={timeSpanStartDate} timeSpanEndDate={timeSpanEndDate} />
                                </Box>
                            </Box>
                            <Box height={tabExpanded ? "460px" : "360px"}
                                backgroundColor={colors.primary[400]}>
                                <Box width="100%" height="100%" paddingTop="10px">
                                    <CountedStopsChart data={machinestopsQuery} xAxisLabel={formatMessage(messages.stopCount)} />
                                </Box>
                            </Box>
                        </Box>}
                    </Box>
                }
                
                {/* Product orders tab */}
                {selectedTab === 2 &&
                    <StartStopPO 
                        machineId={machineId}
                        tabExpanded={tabExpanded}
                        machineInfo={machineInfo}
                        machine={machineQuery?.data}
                        invalidateMachineInfo={invalidateMachineInfo}
                        reportPO={reportPO}
                        setReportPO={setReportPO}
                        featureFlags={featuresQuery}
                    />

                }

                {selectedTab === 3 &&
                    <ActivitiesGrid
                        activitiesResult={activitiesQuery}
                        filteredResult={filteredActivitiesResult}
                        InitialState={gridInitialState}
                        activityFilter={activityFilter}
                        setActivityFilter={setActivityFilter}
                        statusFilter={statusFilter}
                        setStatusFilter={setStatusFilter}
                        hideStatusFilter={false}
                        tabExpanded={tabExpanded}
                    />
                }

                {selectedTab === 4 &&
                    <MachinePace
                        machineId={machineId}
                        machineQuery={machineQuery}
                        tabExpanded={tabExpanded}
                    />
                }

                {selectedTab === 5 &&
                    <SensorData
                        machineId={machineId}
                        machineName={machineQuery.data?.machineName}
                        timespan={timeSpan}
                        startDate={currentStartDate}
                        endDate={currentEndDate}
                        timeSpanStartDate={timeSpanStartDate}
                        timeSpanEndDate={timeSpanEndDate}
                        tabExpanded={tabExpanded}
                    />
                }

                {selectedTab === 6 &&
                    <AlarmsGrid
                        activitiesResult={activitiesQuery}
                        refetchActivities={activitiesQuery?.refetch}
                        filteredResult={filteredActivitiesResult}
                        InitialState={gridInitialState}
                        activityFilter={activityFilter}
                        setActivityFilter={setActivityFilter}
                        statusFilter={statusFilter}
                        setStatusFilter={setStatusFilter}
                        hideStatusFilter={false}
                        readingTypesResult={readingTypesQuery}
                        tabExpanded={tabExpanded}
                    />
                }

                {/* Guarded on availability as well as the index: the body must never render for
                    a user whose module or role was taken away, and mounting it only while selected
                    keeps an unopened tab free of requests. */}
                {selectedTab === CMMS_WORK_ORDERS_TAB_INDEX && cmmsWorkOrderTabAvailable &&
                    <MachineWorkOrdersTab
                        machineId={machineId}
                        companyKey={companyKey}
                    />
                }

                <Popup
                    openPopup={openCommentPopup}
                    setOpenPopup={setOpenCommentPopup}
                    title={<FormattedMessage {...messages.editComment} />}>
                    <MachineCommentDialog
                        setOpenPopup={setOpenCommentPopup}
                        setShowBackdrop={setShowBackdrop}
                        onCommentCancel={handleCommentCancel}
                        refresh={handleRefresh}
                        machine={machineQuery.data}
                    />
                </Popup>
                <Popup
                    openPopup={openSettingsPopup}
                    setOpenPopup={setOpenSettingsPopup}
                    title={<FormattedMessage {...messages.editSettings} />}>
                    <MachineSettingsDialog
                        setOpenPopup={setOpenSettingsPopup}
                        stopReasonCodes={stopReasonCodesQuery.data}
                        machineData={machineSettings}
                        areas={aresQuery.data}
                        machineTypes={machineTypes.data}
                        featuresQuery={featuresQuery}
                        speedUnitTypes={speedUnitTypesQuery.data}
                        setMachineSettings={setMachineSettings}
                        onSettingsSave={handleSettingsSave}
                        onSettingsCancel={handleSettingsCancel} />
                </Popup>
                <Popup
                    openPopup={openPlclogPopup}
                    setOpenPopup={setopenPlclogPopup}
                    title="PLC Log">
                    {isFeatureEnabled(featuresQuery, "UseMachineIdForPlcLoggQueries") &&
                        <PlclogDialog
                            machineId={machineId}
                        />
                    }
                    {!isFeatureEnabled(featuresQuery, "UseMachineIdForPlcLoggQueries") &&
                        <PlclogDialog
                            machineName={machineName}
                        />
                    }
                </Popup>
                <Popup
                    openPopup={newActivityPopup}
                    setOpenPopup={setNewActivityPopup}
                    title={<FormattedMessage {...messages.newActivity} />}>
                    <NewActivityDialog
                        setOpenPopup={setNewActivityPopup}
                        activitiesQuery={activitiesQuery}
                        setSelectedTab={props.setSelectedTab}
                    />
                </Popup>
                <Popup
                    openPopup={openAutoCodePopup}
                    setOpenPopup={setOpenAutoCodePopup}
                    title={<FormattedMessage {...messages.autocode} />}>
                    <AutoCodeDialog
                        setOpenPopup={setOpenAutoCodePopup}
                        stopReasonCodes={filteredStopReasonCodes}
                        machineId={machineId}
                        setShowBackdrop={setShowBackdrop}
                        refresh={handleRefresh}
                        handleCancelAutocodeClick={handleCancelAutocodeClick}
                        autocodeIsActive={machineQuery.data.autocodeStopReasonCodeId !== null}
                    />
                </Popup>
                <Popup
                    openPopup={machineStopPopup}
                    setOpenPopup={setMachineStopPopup}
                    maxWidht="sm"
                    title={<FormattedMessage {...messages.editStop} />}>
                    <MachineStopEditDialog
                        stopReasonCodes={filteredStopReasonCodes}
                        refresh={handleStopRefresh}
                        machineStop={machineStop}
                        handleSplit={handleSplit}
                        setOpenPopup={setMachineStopPopup}
                        setShowBackdrop={setShowBackdrop}
                    />
                </Popup>

                <AxionControls.AxionBackdrop open={showBackdrop} />
            </Box >
    }

    return (
        <>
            {content}
        </>
    )
}

export default MachineDetails;
